import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { AuthRequestModel } from 'src/app/models/auth/auth-request';
import { AuthResponseModel } from 'src/app/models/auth/auth-response';
import { ResetPasswordModel } from 'src/app/models/auth/reset-password';
import { UserAccountModel } from 'src/app/models/user-account/user-account';
import { UserAccountTypeInnerModel } from 'src/app/models/user-account-type/user-account-type-inner';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import swal from 'sweetalert2';

@Injectable({
  providedIn: 'root'
})
export class AuthServiceService {
  private readonly sessionStorageKeys: string[] = ['token', 'userAccountId', 'username', 'userAccountType'];
  private readonly idleTimeoutInMilliseconds: number = 30 * 60 * 1000; // 30 minutes
  private readonly activityEvents: string[] = ['click', 'keydown', 'mousedown', 'mousemove', 'scroll', 'touchstart'];

  private baseURL: string = environment.apiURL + '/v1/auth';
  private isHandlingSessionInvalidation: boolean = false;
  private idleTimeoutId: number | null = null;
  private hasSessionMonitoringInitialized: boolean = false;

  constructor(
    private httpClient: HttpClient,
    private router: Router,
  ) {
    this.migrateLegacySessionToSessionStorage();
  }

  login(dto: AuthRequestModel): Observable<AuthResponseModel> {
    return this.httpClient.post<AuthResponseModel>(this.baseURL + '/login', dto);
  }
  resetPassword(dto:ResetPasswordModel): Observable<AuthResponseModel> {
    return this.httpClient.patch<AuthResponseModel>(this.baseURL + '/reset-password', dto);
  }
  isLoggedIn() {
    return !!this.getToken() && this.getStoredUserAccountId() !== null;
  }
  getToken() {
    return this.getStoredSessionValue('token');
  }
  getStoredUserAccountId(): number | null {
    const rawUserAccountId = this.getStoredSessionValue('userAccountId');

    if (!rawUserAccountId) {
      return null;
    }

    try {
      const parsedUserAccountId = JSON.parse(rawUserAccountId);
      const userAccountId = Number(parsedUserAccountId);
      return Number.isFinite(userAccountId) ? userAccountId : null;
    } catch {
      const userAccountId = Number(rawUserAccountId);
      return Number.isFinite(userAccountId) ? userAccountId : null;
    }
  }
  getStoredUsername(): string | null {
    return this.getParsedSessionValue<string>('username');
  }
  getStoredUserAccountType(): UserAccountTypeInnerModel | null {
    return this.getParsedSessionValue<UserAccountTypeInnerModel>('userAccountType');
  }
  isCmsAccessAllowed(userAccount?: UserAccountModel): boolean {
    return !!userAccount && !userAccount.isDeactivated && !!userAccount.isLoginCmsEnabled;
  }
  storeSession(authResponse: AuthResponseModel): void {
    this.clearSession();

    if (authResponse.token) {
      sessionStorage.setItem('token', authResponse.token);
    }

    sessionStorage.setItem('userAccountId', JSON.stringify(authResponse.user?.userAccountId ?? null));
    sessionStorage.setItem('username', JSON.stringify(authResponse.user?.username ?? null));
    sessionStorage.setItem('userAccountType', JSON.stringify(authResponse.user?.userAccountType ?? null));

    this.resetIdleTimer();
  }
  clearSession(): void {
    this.clearIdleTimer();
    this.sessionStorageKeys.forEach((key) => {
      sessionStorage.removeItem(key);
      localStorage.removeItem(key);
    });
  }
  logout(): Promise<boolean> {
    this.clearSession();
    return this.router.navigateByUrl('/login');
  }
  invalidateSession(
    title: string = 'Auto Logged Out',
    message: string = 'You have been logged out because your CMS access is no longer valid.',
  ): void {
    if (this.isHandlingSessionInvalidation) {
      return;
    }

    this.isHandlingSessionInvalidation = true;
    this.clearSession();

    this.router.navigateByUrl('/login').finally(() => {
      swal.fire(title, message, 'info').finally(() => {
        this.isHandlingSessionInvalidation = false;
      });
    });
  }
  initializeSessionMonitoring(): void {
    if (this.hasSessionMonitoringInitialized || typeof window === 'undefined') {
      return;
    }

    this.hasSessionMonitoringInitialized = true;
    this.activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, this.handleUserActivity, true);
    });
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    this.resetIdleTimer();
  }
  private readonly handleUserActivity = (): void => {
    this.resetIdleTimer();
  };
  private readonly handleVisibilityChange = (): void => {
    if (!document.hidden) {
      this.resetIdleTimer();
    }
  };
  private getStoredSessionValue(key: string): string | null {
    if (typeof window === 'undefined') {
      return null;
    }

    const sessionValue = sessionStorage.getItem(key);

    if (sessionValue !== null) {
      return sessionValue;
    }

    const localValue = localStorage.getItem(key);

    if (localValue !== null) {
      sessionStorage.setItem(key, localValue);
      localStorage.removeItem(key);
    }

    return localValue;
  }
  private getParsedSessionValue<T>(key: string): T | null {
    const rawValue = this.getStoredSessionValue(key);

    if (rawValue === null) {
      return null;
    }

    try {
      return JSON.parse(rawValue) as T;
    } catch {
      return rawValue as unknown as T;
    }
  }
  private migrateLegacySessionToSessionStorage(): void {
    if (typeof window === 'undefined') {
      return;
    }

    this.sessionStorageKeys.forEach((key) => {
      const sessionValue = sessionStorage.getItem(key);
      const localValue = localStorage.getItem(key);

      if (sessionValue === null && localValue !== null) {
        sessionStorage.setItem(key, localValue);
      }

      if (localValue !== null) {
        localStorage.removeItem(key);
      }
    });
  }
  private resetIdleTimer(): void {
    this.clearIdleTimer();

    if (!this.isLoggedIn() || typeof window === 'undefined') {
      return;
    }

    this.idleTimeoutId = window.setTimeout(() => {
      if (this.isLoggedIn()) {
        this.invalidateSession(
          'Auto Logged Out',
          'You have been logged out after 30 minutes of inactivity.',
        );
      }
    }, this.idleTimeoutInMilliseconds);
  }
  private clearIdleTimer(): void {
    if (this.idleTimeoutId !== null) {
      window.clearTimeout(this.idleTimeoutId);
      this.idleTimeoutId = null;
    }
  }
}
